/**
 * @name useDualReceiptPrinter
 * @author Keven Adan Bezerra | kevenadanb
 * @description Hook para impressão dual de via do pedido (Totem) e comandas (Cozinha)
 * @returns {DualReceiptPrinterHookReturn} Métodos para impressão com múltiplas impressoras
 * @package src/hooks/useDualReceiptPrinter.ts
 */

import { useCallback } from "react";
import { DualPrintResult } from "../types/printer";
import { useAlert } from "./useAlert";
import { useMultiplePrinters } from "./useMultiplePrinters";

/**
 * Remove caracteres especiais de uma string para impressão
 */
const removeSpecialChars = (text: string): string => {
  return text
    .normalize("NFD") // Normaliza caracteres acentuados
    .replace(/[\u0300-\u036f]/g, "") // Remove acentos
    .replace(/[çÇ]/g, "c") // Replace ç/Ç
    .replace(/[^\w\s.-]/g, "") // Remove outros caracteres especiais, mantendo letras, números, espaços, pontos e hífens
    .trim();
};

// Tipos para dados do recibo
interface ItemRecibo {
  nome: string;
  quantidade: number;
  valor_unitario: number;
  valor_total: number;
}

interface DadosRecibo {
  numero_pedido: string;
  data_hora: string;
  aluno_nome: string;
  aluno_matricula: string;
  itens: ItemRecibo[];
  valor_total: number;
  operador_nome: string;
  organizacao_nome: string;
}

interface DualReceiptPrinterHookReturn {
  /** Imprime via do pedido completa na impressora do totem (para o aluno) */
  imprimirRecibo: (dados: DadosRecibo) => Promise<boolean>;
  /** Imprime comanda simplificada na impressora da cozinha */
  imprimirComanda: (dados: DadosRecibo) => Promise<boolean>;
  /** Imprime tanto via do pedido quanto comanda nas respectivas impressoras */
  imprimirDual: (dados: DadosRecibo) => Promise<DualPrintResult>;
  /** Testa impressão de via do pedido na impressora do totem */
  imprimirTesteRecibo: () => Promise<boolean>;
  /** Testa impressão de comanda na impressora da cozinha */
  imprimirTesteComanda: () => Promise<boolean>;
  /** Força detecção de impressoras */
  detectarImpressoras: () => Promise<boolean>;
}

/**
 * Hook para impressão dual de via do pedido e comandas
 */
export const useDualReceiptPrinter = (): DualReceiptPrinterHookReturn => {
  const { selectedPrinters, isConnected, connectToQZTray, getPrinters } =
    useMultiplePrinters();
  const { showError, showSuccess, showWarning } = useAlert();

  /**
   * Formata valor monetário
   */
  const formatMoney = useCallback((value: number): string => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  }, []);

  /**
   * Gera conteúdo da via do pedido completa (para o aluno)
   */
  const gerarConteudoRecibo = useCallback(
    (dados: DadosRecibo): string[] => {
      const conteudo: string[] = [];

      // Cabeçalho
      conteudo.push("\x1B\x40"); // Reset printer
      conteudo.push("\x1B\x61\x01"); // Center alignment
      conteudo.push("\x1B\x21\x30"); // Double size
      conteudo.push(`${dados.organizacao_nome}\n`);
      conteudo.push("\x1B\x21\x00"); // Normal size
      conteudo.push("Sagrada Familia\n");
      conteudo.push("VIA DO PEDIDO\n");
      conteudo.push("\x1B\x61\x00"); // Left alignment
      conteudo.push("\n");

      // Linha separadora
      conteudo.push("================================\n");

      // Dados do pedido
      conteudo.push(`Pedido: ${dados.numero_pedido}\n`);
      conteudo.push(`Data/Hora: ${dados.data_hora}\n`);
      conteudo.push(`Aluno: ${dados.aluno_nome}\n`);
      conteudo.push(`Matricula: ${dados.aluno_matricula}\n`);
      conteudo.push(`Operador: ${dados.operador_nome}\n`);
      conteudo.push("================================\n");

      // Itens
      conteudo.push("ITENS:\n");
      dados.itens.forEach((item) => {
        const nomeItem = removeSpecialChars(item.nome);
        const nomeFormatado =
          nomeItem.length > 20 ? nomeItem.substring(0, 17) + "..." : nomeItem;

        conteudo.push(`${item.quantidade}x ${nomeFormatado}\n`);
        conteudo.push(
          `   ${formatMoney(item.valor_unitario)} = ${formatMoney(item.valor_total)}\n`,
        );
      });

      conteudo.push("================================\n");

      // Total
      conteudo.push("\x1B\x21\x10"); // Double width
      conteudo.push(`TOTAL: ${formatMoney(dados.valor_total)}\n`);
      conteudo.push("\x1B\x21\x00"); // Normal size

      // Rodapé
      conteudo.push("\n");
      conteudo.push("Obrigado pela preferencia!\n");
      conteudo.push("Guarde esta via\n");
      conteudo.push("\n");
      conteudo.push("\x1D\x56\x00"); // Cut paper

      return conteudo;
    },
    [formatMoney],
  );

  /**
   * Gera conteúdo da comanda simplificada (para a cozinha)
   */
  const gerarConteudoComanda = useCallback(
    (dados: DadosRecibo): string[] => {
      const conteudo: string[] = [];

      // Cabeçalho
      conteudo.push("\x1B\x40"); // Reset printer
      conteudo.push("\x1B\x61\x01"); // Center alignment
      conteudo.push("\x1B\x21\x30"); // Double size
      conteudo.push("COMANDA - COZINHA\n");
      conteudo.push("\x1B\x21\x00"); // Normal size
      conteudo.push("\x1B\x61\x00"); // Left alignment
      conteudo.push("\n");

      // Dados básicos
      conteudo.push("========================\n");
      conteudo.push(`Pedido: ${dados.numero_pedido}\n`);
      conteudo.push(`Aluno: ${dados.aluno_nome}\n`);
      conteudo.push(
        `Hora: ${dados.data_hora.split(" ")[1] || dados.data_hora}\n`,
      );
      conteudo.push("========================\n");

      // Itens para preparo
      dados.itens.forEach((item) => {
        const nomeItem = removeSpecialChars(item.nome);
        conteudo.push(`${item.quantidade}x ${nomeItem}\n`);
      });

      conteudo.push("========================\n");
      conteudo.push(`Total: ${formatMoney(dados.valor_total)}\n`);
      conteudo.push("\n");
      conteudo.push("\x1D\x56\x00"); // Cut paper

      return conteudo;
    },
    [formatMoney],
  );

  /**
   * Executa impressão em uma impressora específica
   */
  const executarImpressao = useCallback(
    async (
      conteudo: string[],
      tipo: string,
      printerToUse: string | null,
    ): Promise<boolean> => {
      console.log(`🖨️ Tentando imprimir ${tipo}...`);
      console.log(
        `📊 Estado atual: {printer: ${printerToUse}, isConnected: ${isConnected}, qzAvailable: ${typeof window.qz !== "undefined"}}`,
      );

      if (!printerToUse) {
        console.warn(`❌ Nenhuma impressora configurada para ${tipo}`);
        showWarning(`Nenhuma impressora configurada para ${tipo}`);
        return false;
      }

      // Verifica se QZ Tray está disponível
      if (typeof window.qz === "undefined") {
        console.warn("❌ QZ Tray não está disponível");
        showError("QZ Tray não está disponível");
        return false;
      }

      // Verifica conexão
      if (!isConnected || !window.qz.websocket.isActive()) {
        console.log("🔄 Tentando reconectar ao QZ Tray...");
        const connected = await connectToQZTray();
        if (!connected) {
          console.warn("❌ Falha ao conectar com QZ Tray");
          return false;
        }
      }

      try {
        console.log(`📄 Criando configuração para impressora: ${printerToUse}`);

        // Configuração da impressora
        const config = window.qz.configs.create(printerToUse, {
          encoding: "CP850", // Codificação para caracteres especiais
        });

        console.log(`📤 Enviando ${tipo} para impressão...`);

        // Enviar para impressão
        await window.qz.print(config, conteudo);

        console.log(`✅ ${tipo} enviado para ${printerToUse} com sucesso!`);
        return true;
      } catch (error: any) {
        console.error(`❌ Erro ao imprimir ${tipo}:`, error);
        throw error;
      }
    },
    [isConnected, connectToQZTray, showWarning, showError],
  );

  /**
   * Imprime recibo completo na impressora do totem
   */
  const imprimirRecibo = useCallback(
    async (dados: DadosRecibo): Promise<boolean> => {
      const conteudo = gerarConteudoRecibo(dados);
      try {
        return await executarImpressao(
          conteudo,
          "Recibo (Totem)",
          selectedPrinters.printerTotem,
        );
      } catch (error) {
        console.error("Erro ao imprimir recibo:", error);
        return false;
      }
    },
    [gerarConteudoRecibo, executarImpressao, selectedPrinters.printerTotem],
  );

  /**
   * Imprime comanda simplificada na impressora da cozinha
   */
  const imprimirComanda = useCallback(
    async (dados: DadosRecibo): Promise<boolean> => {
      const conteudo = gerarConteudoComanda(dados);
      try {
        return await executarImpressao(
          conteudo,
          "Comanda (Cozinha)",
          selectedPrinters.printerCozinha,
        );
      } catch (error) {
        console.error("Erro ao imprimir comanda:", error);
        return false;
      }
    },
    [gerarConteudoComanda, executarImpressao, selectedPrinters.printerCozinha],
  );

  /**
   * Imprime tanto recibo quanto comanda nas respectivas impressoras
   */
  const imprimirDual = useCallback(
    async (dados: DadosRecibo): Promise<DualPrintResult> => {
      console.log("🔄 Iniciando impressão dual (Recibo + Comanda)...");

      // 🛡️ Identificador único para esta impressão
      const impressaoId = `${dados.numero_pedido}_${Date.now()}`;
      console.log("🆔 ID da impressão:", impressaoId);

      // 🛡️ Controle global de impressão para evitar execuções simultâneas
      const chaveControle = `impressao_dual_${dados.numero_pedido}`;

      // Verificar se já existe uma impressão em andamento para este pedido
      if ((window as any)[chaveControle]) {
        console.warn(
          "🚫 Impressão dual já em andamento para pedido:",
          dados.numero_pedido,
        );
        return { recibo: false, comanda: false };
      }

      // Marcar como em execução
      (window as any)[chaveControle] = true;

      try {
        // Executa ambas as impressões em paralelo
        const [reciboResult, comandaResult] = await Promise.allSettled([
          imprimirRecibo(dados),
          imprimirComanda(dados),
        ]);

        const resultado: DualPrintResult = {
          recibo: reciboResult.status === "fulfilled" && reciboResult.value,
          comanda: comandaResult.status === "fulfilled" && comandaResult.value,
        };

        // Log detalhado dos resultados
        console.log("📊 Resultado detalhado da impressão dual:", {
          impressaoId,
          reciboStatus: reciboResult.status,
          reciboValor:
            reciboResult.status === "fulfilled" ? reciboResult.value : "erro",
          reciboErro:
            reciboResult.status === "rejected" ? reciboResult.reason : null,
          comandaStatus: comandaResult.status,
          comandaValor:
            comandaResult.status === "fulfilled" ? comandaResult.value : "erro",
          comandaErro:
            comandaResult.status === "rejected" ? comandaResult.reason : null,
          resultadoFinal: resultado,
        });

        // Feedback ao usuário baseado no resultado
        if (resultado.recibo && resultado.comanda) {
          showSuccess("Recibo e comanda impressos com sucesso");
        } else if (resultado.recibo || resultado.comanda) {
          showWarning(
            "Impressão parcial realizada. Verifique configurações das impressoras.",
          );
        } else {
          showError(
            "Falha na impressão. Verifique se as impressoras estão configuradas.",
          );
        }

        return resultado;
      } finally {
        // Limpar o controle após um delay para evitar problemas de timing
        setTimeout(() => {
          delete (window as any)[chaveControle];
          console.log(
            "🧹 Limpeza do controle de impressão dual para:",
            dados.numero_pedido,
          );
        }, 2000);
      }
    },
    [imprimirRecibo, imprimirComanda, showSuccess, showWarning, showError],
  ); /**
   * Testa impressão de via do pedido na impressora do totem
   */
  const imprimirTesteRecibo = useCallback(async (): Promise<boolean> => {
    const dadosTeste: DadosRecibo = {
      numero_pedido: "TEST-" + Date.now(),
      data_hora: new Date().toLocaleString("pt-BR"),
      aluno_nome: "Teste Aluno",
      aluno_matricula: "123456",
      operador_nome: "Operador Teste",
      organizacao_nome: "Escola Teste",
      itens: [
        {
          nome: "Lanche Natural",
          quantidade: 1,
          valor_unitario: 8.5,
          valor_total: 8.5,
        },
        {
          nome: "Suco de Laranja",
          quantidade: 1,
          valor_unitario: 4.0,
          valor_total: 4.0,
        },
      ],
      valor_total: 12.5,
    };

    return await imprimirRecibo(dadosTeste);
  }, [imprimirRecibo]);

  /**
   * Testa impressão de comanda na impressora da cozinha
   */
  const imprimirTesteComanda = useCallback(async (): Promise<boolean> => {
    const dadosTeste: DadosRecibo = {
      numero_pedido: "TEST-" + Date.now(),
      data_hora: new Date().toLocaleString("pt-BR"),
      aluno_nome: "Teste Aluno - Cozinha",
      aluno_matricula: "123456",
      operador_nome: "Operador Teste",
      organizacao_nome: "Escola Teste",
      itens: [
        {
          nome: "Lanche Natural",
          quantidade: 1,
          valor_unitario: 8.5,
          valor_total: 8.5,
        },
        {
          nome: "Suco de Laranja",
          quantidade: 1,
          valor_unitario: 4.0,
          valor_total: 4.0,
        },
      ],
      valor_total: 12.5,
    };

    return await imprimirComanda(dadosTeste);
  }, [imprimirComanda]);

  /**
   * Força detecção de impressoras
   */
  const detectarImpressoras = useCallback(async (): Promise<boolean> => {
    console.log("🔍 Forçando detecção de impressoras...");

    try {
      const impressoras = await getPrinters();
      const encontradas = impressoras.length > 0;

      if (encontradas) {
        showSuccess(`${impressoras.length} impressora(s) detectada(s)`);
      } else {
        showWarning("Nenhuma impressora detectada");
      }

      return encontradas;
    } catch (error) {
      console.error("Erro ao detectar impressoras:", error);
      showError("Erro ao detectar impressoras");
      return false;
    }
  }, [getPrinters, showSuccess, showWarning, showError]);

  return {
    imprimirRecibo,
    imprimirComanda,
    imprimirDual,
    imprimirTesteRecibo,
    imprimirTesteComanda,
    detectarImpressoras,
  };
};
